import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Menu, X, Music, Users, Mail, FileText, Home as HomeIcon,
  Instagram, ExternalLink, Disc3, Search
} from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Pages
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import ArtistsPage from "./pages/ArtistsPage";
import ContactPage from "./pages/ContactPage";
import LegalPage from "./pages/LegalPage";
import AdminPage from "./pages/AdminPage";
import SelectaPage from "./pages/SelectaPage";
import DemoPage from "./pages/DemoPage";
import NotFoundPage from "./pages/NotFoundPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Audio Player Context
const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

// Audio Provider
const AudioProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const playTrack = (track, tracks = []) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    if (tracks.length > 0) {
      setPlaylist(tracks);
      const index = tracks.findIndex(t => t.id === track.id);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  };

  const togglePlay = () => setIsPlaying(!isPlaying);
  
  const nextTrack = () => {
    if (playlist.length > 0) {
      const nextIndex = (currentIndex + 1) % playlist.length;
      setCurrentIndex(nextIndex);
      setCurrentTrack(playlist[nextIndex]);
    }
  };

  const prevTrack = () => {
    if (playlist.length > 0) {
      const prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
      setCurrentIndex(prevIndex);
      setCurrentTrack(playlist[prevIndex]);
    }
  };

  const toggleMute = () => setIsMuted(!isMuted);

  return (
    <AudioContext.Provider value={{
      currentTrack, isPlaying, volume, isMuted, playlist,
      playTrack, togglePlay, nextTrack, prevTrack, toggleMute, setVolume
    }}>
      {children}
    </AudioContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const { t } = useTranslation();

  const navLinks = [
    { to: "/", label: t('nav.home'), icon: HomeIcon },
    { to: "/catalogue", label: t('nav.catalog'), icon: Music },
    { to: "/artistes", label: t('nav.artists'), icon: Users },
    { to: "/selecta", label: "Sélecta", icon: Music },
    { to: "/contact", label: t('nav.contact'), icon: Mail },
    { to: "/mentions-legales", label: t('nav.legal'), icon: FileText },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass border-b border-[rgba(139,69,19,0.2)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Click to access admin */}
          <div className="flex items-center gap-3">
            <Link to="/admin" className="block group" data-testid="logo-admin-link" title="Administration">
              <img 
                src="https://customer-assets.emergentagent.com/job_78551dcc-3f96-482d-9a37-f271494e00a9/artifacts/lb374gtc_Image%20PNG-475F-90F1-76-0.png" 
                alt="Sapito-Records" 
                className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-300"
              />
            </Link>
            <Link to="/" className="font-display text-xl font-bold tracking-tight hover:text-[#FFB347] transition-colors flex items-center">
              <span>Sapito</span><span className="bg-gradient-to-r from-[#F5F5F0] to-[#FFB347] bg-clip-text text-transparent text-[0.6em] ml-0.5 mr-0 relative top-[0.05em]">-</span><span className="text-[#FFB347]">Records</span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-display text-lg transition-colors hover:text-[#FFB347] ${
                  location.pathname === link.to ? 'text-[#FFB347]' : 'text-[#F5F5F0]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-[#F5F5F0] hover:text-[#FFB347] transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-b border-[rgba(139,69,19,0.2)]"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`flex items-center gap-3 py-2 font-display text-lg transition-colors hover:text-[#FFB347] ${
                    location.pathname === link.to ? 'text-[#FFB347]' : 'text-[#F5F5F0]'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Audio Player Bar Component
const AudioPlayerBar = () => {
  const { currentTrack, isPlaying, togglePlay, nextTrack, prevTrack, playlist } = useAudio();
  const [showPlayer, setShowPlayer] = useState(false);

  if (!currentTrack) return null;

  // SoundCloud embed URL for widget
  const getEmbedUrl = (track) => {
    if (!track.soundcloud_url) return null;
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(track.soundcloud_url)}&color=%23FFB347&auto_play=true&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`;
  };

  const embedUrl = getEmbedUrl(currentTrack);

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="audio-player-bar"
      data-testid="audio-player"
    >
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Track Info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {currentTrack.cover_image ? (
              <img 
                src={currentTrack.cover_image} 
                alt={currentTrack.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#FFB347] to-[#8B4513] flex items-center justify-center">
                <Disc3 className="w-6 h-6 text-[#0A0908]" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-display font-semibold truncate text-[#F5F5F0]" data-testid="current-track-title">
                {currentTrack.title}
              </p>
              <p className="text-sm text-[#A8A29E] truncate" data-testid="current-track-artist">
                {currentTrack.artist}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={prevTrack}
              className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
              data-testid="prev-track-btn"
            >
              <SkipBack className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowPlayer(!showPlayer)}
              className="w-12 h-12 rounded-full bg-[#FFB347] text-[#0A0908] flex items-center justify-center hover:bg-[#FFC570] transition-all hover:scale-105"
              data-testid="play-pause-btn"
            >
              {showPlayer ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button
              onClick={nextTrack}
              className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
              data-testid="next-track-btn"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          </div>

          {/* Track count */}
          <div className="hidden sm:flex items-center gap-2 flex-1 justify-end text-[#A8A29E] text-sm">
            {playlist.length > 0 && (
              <span>{playlist.findIndex(t => t.id === currentTrack.id) + 1} / {playlist.length}</span>
            )}
          </div>
        </div>

        {/* SoundCloud Player */}
        {showPlayer && embedUrl && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 80, opacity: 1 }}
            className="mt-4 rounded-lg overflow-hidden"
          >
            <iframe
              width="100%"
              height="80"
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
              src={embedUrl}
              title={currentTrack.title}
            />
          </motion.div>
        )}

        {showPlayer && !embedUrl && (
          <div className="mt-4 p-3 bg-[#0A0908] rounded-lg text-center text-[#A8A29E] text-sm">
            Pas de lecteur disponible pour cette track
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Merci ! Vous êtes inscrit à la newsletter.');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Cet email est déjà inscrit.');
      } else {
        toast.error('Erreur lors de l\'inscription.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0A0908] border-t border-[rgba(139,69,19,0.2)] pt-16 pb-32" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/admin" title="Administration">
                <img 
                  src="https://customer-assets.emergentagent.com/job_78551dcc-3f96-482d-9a37-f271494e00a9/artifacts/lb374gtc_Image%20PNG-475F-90F1-76-0.png" 
                  alt="Sapito-Records" 
                  className="w-12 h-12 object-contain hover:scale-110 transition-transform"
                />
              </Link>
              <span className="font-display text-xl font-bold flex items-center">
                <span>Sapito</span><span className="bg-gradient-to-r from-[#F5F5F0] to-[#FFB347] bg-clip-text text-transparent text-[0.6em] ml-0.5 mr-0 relative top-[0.05em]">-</span><span className="text-[#FFB347]">Records</span>
              </span>
            </div>
            <p className="text-[#A8A29E] mb-6 max-w-md">
              {t('footer.description')}
            </p>
            
            {/* Newsletter */}
            <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="input-field flex-1"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {loading ? '...' : t('footer.subscribe')}
              </button>
            </form>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading text-sm text-[#FFB347] mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-3">
              <li><Link to="/catalogue" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors" data-testid="footer-catalogue">{t('nav.catalog')}</Link></li>
              <li><Link to="/artistes" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors" data-testid="footer-artistes">{t('nav.artists')}</Link></li>
              <li><Link to="/contact" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors" data-testid="footer-contact">{t('nav.contact')}</Link></li>
              <li><Link to="/mentions-legales" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors" data-testid="footer-legal">{t('nav.legal')}</Link></li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading text-sm text-[#FFB347] mb-4">{t('footer.followUs')}</h4>
            <div className="flex gap-4">
              <a 
                href="https://soundcloud.com/sapito-records" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-[rgba(139,69,19,0.3)] flex items-center justify-center text-[#A8A29E] hover:text-[#FFB347] hover:border-[#FFB347] transition-all"
                data-testid="social-soundcloud"
              >
                <Music className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full border border-[rgba(139,69,19,0.3)] flex items-center justify-center text-[#A8A29E] hover:text-[#FFB347] hover:border-[#FFB347] transition-all"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-[rgba(139,69,19,0.2)] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#A8A29E] text-sm">
            © {new Date().getFullYear()} Sapito-Records. {t('footer.rights')}
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/mentions-legales" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors">
              {t('legal.title')}
            </Link>
            <Link to="/mentions-legales#cgv" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors">
              {t('legal.terms')}
            </Link>
            <Link to="/mentions-legales#confidentialite" className="text-[#A8A29E] hover:text-[#FFB347] transition-colors">
              Confidentialité
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Page Wrapper with animations
const PageWrapper = ({ children }) => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Main App
function App() {
  return (
    <AudioProvider>
      <div className="App min-h-screen bg-[#0A0908]">
        <BrowserRouter>
          <AppContent />

          <footer className="mt-16 border-t border-white/10 py-6 text-center text-sm text-white/60">
            <span>Partenaire : </span>
            <a
              href="https://app.emergent.sh/?via=claudio-fabiani"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-white"
            >
              Emergent
            </a>
            <span> — </span>
            <span>-5% avec le code </span>
            <span className="font-mono text-white">CLAUDIO</span>
          </footer>

        </BrowserRouter>
      </div>
    </AudioProvider>
  );
}

// Separate component to use useLocation inside BrowserRouter
const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdmin && <Navigation />}
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#141210',
            color: '#F5F5F0',
            border: '1px solid rgba(139, 69, 19, 0.3)',
          },
        }}
      />
      <main className={isAdmin ? "" : "pt-20"}>
        <PageWrapper>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/catalogue" element={<CatalogPage />} />
            <Route path="/artistes" element={<ArtistsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/mentions-legales" element={<LegalPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/selecta" element={<SelectaPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </PageWrapper>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <AudioPlayerBar />}
    </>
  );
}

export default App;
